import { useCallback, useEffect, useMemo, useState } from "react";
import { OrderDetailDialog } from "@/components/admin/OrderDetailDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, FileUp } from "lucide-react";
import OrderImportDialog from "@/components/admin/OrderImportDialog";
import { CLIENT_TYPE_LABELS } from "@/lib/clientTypeLabels";
import {
  ORDER_STATUS_COLORS,
  ORDER_STATUS_LABELS,
  ORDER_STATUS_ORDER,
  orderItemsPreview,
  orderReference,
  orderTitle,
  timeSince,
} from "@/lib/orderUtils";
import {
  fetchAdminOrder,
  fetchAdminOrderSummaries,
  startOrderPurchase,
  toOrderSummaryFromDetail,
  updatePurchaseListItem,
  updateExpeditionListItem,
  updateProductionListItem,
  updateOrderStatus,
  transitionOrder,
} from "@/lib/ordersApi";
import { cn } from "@/lib/utils";
import { Loader2, Search, Tag } from "lucide-react";
import { OrderLabelsDialog } from "@/components/admin/OrderLabelsDialog";
import { canSeeOrderPricing, hasPermission, type FulfillmentMode, type OrderDetail, type OrderStatus, type OrderSummary, type ProductionItemStatus } from "@/lib/types";
import { useApp } from "@/store/useApp";

export default function AdminOperations() {
  const showPricing = canSeeOrderPricing(useApp((s) => s.account));
  const canImport = hasPermission(useApp((s) => s.account), "PEDIDOS.PEDIDO.IMPORTAR");
  const canGenerateLabels = hasPermission(useApp((s) => s.account), "ETIQUETAS.ETIQUETA.GERAR");
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<OrderStatus | "ALL">("ALL");
  const [clientFilter, setClientFilter] = useState<string>("ALL");
  const [clientPopoverOpen, setClientPopoverOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [labelOrderId, setLabelOrderId] = useState<string | null>(null);

  const loadOrders = useCallback(async () => {
    setLoadState("loading");
    setLoadError(null);
    try {
      const summaries = await fetchAdminOrderSummaries();
      setOrders(summaries);
      setLoadState("ready");
    } catch (error) {
      setOrders([]);
      setLoadState("error");
      setLoadError(error instanceof Error ? error.message : "Nao foi possivel carregar os pedidos");
    }
  }, []);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  const openOrder = async (orderId: string) => {
    setDetailLoading(true);
    setSelectedOrder(null);
    try {
      const detail = await fetchAdminOrder(orderId);
      setSelectedOrder(detail);
    } catch {
      setSelectedOrder(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusChange = async (
    orderId: string,
    status: OrderStatus,
    transitionDate?: string,
    driverId?: string,
    fulfillmentMode?: FulfillmentMode,
    conference?: { nfNumber: string; boletoConfirmed: boolean; boletoNote: string; expectedTotal?: number; expectedSignature?: string },
  ) => {
    const updated = transitionDate
      ? await transitionOrder(orderId, {
          toStatus: status, transitionDate, driverId, fulfillmentMode,
          nfNumber: conference?.nfNumber.trim() || undefined,
          boletoConfirmed: conference?.boletoConfirmed,
          boletoNote: conference?.boletoNote.trim() || undefined,
          expectedTotal: conference?.expectedTotal,
          expectedSignature: conference?.expectedSignature,
        })
      : await updateOrderStatus(orderId, status);
    replaceOrder(updated);
  };

  const handleStartPurchase = async (orderId: string) => {
    const updated = await startOrderPurchase(orderId);
    replaceOrder(updated);
  };

  const handleUpdatePurchaseListItem = async (
    orderId: string,
    input: {
      productId: string;
      unit: string;
      purchased: boolean;
      useStock?: boolean;
      purchasedQuantity?: number;
      reason?: string;
    },
  ) => {
    const updated = await updatePurchaseListItem(orderId, input);
    replaceOrder(updated);
  };

  const handleUpdateProductionListItem = async (
    orderId: string,
    input: {
      productId: string;
      unit: string;
      status: ProductionItemStatus;
      useStock?: boolean;
      producedQuantity?: number;
      reason?: string;
    },
  ) => {
    const updated = await updateProductionListItem(orderId, input);
    replaceOrder(updated);
  };

  const handleUpdateExpeditionListItem = async (
    orderId: string,
    input: {
      productId: string;
      unit: string;
      status: "PENDENTE" | "CARREGADO" | "EM_FALTA";
      loadedQuantity?: number;
      reason?: string;
    },
  ) => {
    const updated = await updateExpeditionListItem(orderId, input);
    replaceOrder(updated);
  };

  const replaceOrder = (updated: OrderDetail) => {
    const summary = toOrderSummaryFromDetail(updated);
    setOrders((current) => current.map((order) => (order.id === updated.id ? summary : order)));
    if (selectedOrder?.id === updated.id) {
      setSelectedOrder(updated);
    }
  };

  const clientOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const order of orders) {
      if (!map.has(order.clientId)) map.set(order.clientId, order.clientName || "(sem razão social)");
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" }));
  }, [orders]);

  const selectedClientName = clientFilter === "ALL"
    ? "Todos os clientes"
    : clientOptions.find((c) => c.id === clientFilter)?.name ?? "Cliente";

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return orders
      .filter((order) => (statusFilter === "ALL" ? true : order.status === statusFilter))
      .filter((order) => (clientFilter === "ALL" ? true : order.clientId === clientFilter))
      .filter((order) => {
        if (!normalizedQuery) return true;
        return (
          orderReference(order).toLowerCase().includes(normalizedQuery) ||
          order.clientName.toLowerCase().includes(normalizedQuery) ||
          ORDER_STATUS_LABELS[order.status].toLowerCase().includes(normalizedQuery) ||
          order.itemPreview.some((item) => item.toLowerCase().includes(normalizedQuery))
        );
      })
      .sort((left, right) => +new Date(right.createdAt) - +new Date(left.createdAt));
  }, [clientFilter, orders, query, statusFilter]);

  return (
    <>
      <section className="bg-gradient-hero text-primary-foreground">
        <div className="container flex flex-wrap items-start justify-between gap-4 py-8">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Operações</h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              Acompanhe os pedidos dos clientes e atualize o status conforme o andamento.
            </p>
          </div>
          {canImport && (
            <Button
              onClick={() => setImportOpen(true)}
              className="gap-2 bg-primary-foreground text-primary shadow-sm hover:bg-primary-foreground/90"
            >
              <FileUp className="h-4 w-4" /> Importar pedido
            </Button>
          )}
        </div>
      </section>

      <div className="container space-y-4 py-6">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar por cliente, item, status ou referencia..."
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as OrderStatus | "ALL")}>
            <SelectTrigger className="w-full sm:w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos os status</SelectItem>
              {ORDER_STATUS_ORDER.map((status) => (
                <SelectItem key={status} value={status}>
                  {ORDER_STATUS_LABELS[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Popover open={clientPopoverOpen} onOpenChange={setClientPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={clientPopoverOpen}
                className="w-full justify-between sm:w-[260px]"
              >
                <span className="truncate">{selectedClientName}</span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[260px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Buscar razão social..." />
                <CommandList>
                  <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="todos os clientes"
                      onSelect={() => {
                        setClientFilter("ALL");
                        setClientPopoverOpen(false);
                      }}
                    >
                      <Check
                        className={cn("mr-2 h-4 w-4", clientFilter === "ALL" ? "opacity-100" : "opacity-0")}
                      />
                      Todos os clientes
                    </CommandItem>
                    {clientOptions.map((client) => (
                      <CommandItem
                        key={client.id}
                        value={client.name}
                        onSelect={() => {
                          setClientFilter(client.id);
                          setClientPopoverOpen(false);
                        }}
                      >
                        <Check
                          className={cn("mr-2 h-4 w-4", clientFilter === client.id ? "opacity-100" : "opacity-0")}
                        />
                        <span className="truncate">{client.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {loadState === "loading" && (
          <div className="surface-card p-12 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-primary" />
            Carregando pedidos...
          </div>
        )}

        {loadState === "error" && (
          <div className="surface-card p-12 text-center text-sm text-destructive">
            {loadError ?? "Nao foi possivel carregar os pedidos"}
          </div>
        )}

        {loadState !== "loading" && loadState !== "error" && (
          <>
            <div className="text-xs text-muted-foreground">
              {filtered.length} pedido{filtered.length === 1 ? "" : "s"}
            </div>

            {filtered.length === 0 ? (
              <div className="surface-card p-12 text-center text-sm text-muted-foreground">
                Nenhum pedido encontrado.
              </div>
            ) : (
              <ul className="surface-card divide-y divide-border overflow-hidden">
                {filtered.map((order) => {
                  const colors = ORDER_STATUS_COLORS[order.status];
                  return (
                    <li key={order.id} className="flex items-center">
                      <button
                        onClick={() => void openOrder(order.id)}
                        className="flex flex-1 items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-secondary/50"
                      >
                        <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", colors.dot)} />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-display text-sm font-bold">{orderTitle(order)}</span>
                            <span className={cn("rounded-md border px-2 py-0.5 text-[11px] font-medium", colors.bg, colors.text, colors.border)}>
                              {ORDER_STATUS_LABELS[order.status]}
                            </span>
                          </div>
                          <div className="mt-1 truncate text-xs text-muted-foreground">
                            {CLIENT_TYPE_LABELS[order.clientType]} · {orderItemsPreview(order)} · {timeSince(order.createdAt)}
                          </div>
                          <div className="mt-0.5 text-[11px] text-muted-foreground/80">
                            {orderReference(order)}
                          </div>
                        </div>
                        {showPricing && order.total !== undefined && (
                          <span className="shrink-0 font-display text-sm font-bold tabular-nums">
                            R$ {order.total.toFixed(2)}
                          </span>
                        )}
                        <Button variant="ghost" size="sm" tabIndex={-1} className="pointer-events-none shrink-0">
                          Abrir
                        </Button>
                      </button>
                      {canGenerateLabels && (
                        <button
                          type="button"
                          title="Gerar etiquetas do pedido"
                          onClick={() => setLabelOrderId(order.id)}
                          className="mr-4 flex shrink-0 items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                        >
                          <Tag className="h-4 w-4" /> Etiquetas
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>

      <OrderDetailDialog
        order={selectedOrder}
        loading={detailLoading}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedOrder(null);
            setDetailLoading(false);
          }
        }}
        onChangeStatus={handleStatusChange}
        onStartPurchase={handleStartPurchase}
        onUpdatePurchaseListItem={handleUpdatePurchaseListItem}
        onUpdateProductionListItem={handleUpdateProductionListItem}
        onUpdateExpeditionListItem={handleUpdateExpeditionListItem}
        onOrderUpdated={replaceOrder}
      />
      <OrderImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={() => loadOrders()} />
      <OrderLabelsDialog open={!!labelOrderId} orderId={labelOrderId} onClose={() => setLabelOrderId(null)} />
    </>
  );
}
